import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tabs,
  Tab,
  Autocomplete,
  Card,
  CardActionArea,
  Divider,
  InputAdornment,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RedeemIcon from '@mui/icons-material/Redeem';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  getCampaigns,
  createCampaign,
  updateCampaign,
  toggleCampaign,
  deleteCampaign,
  getEcommerceProducts,
  getPublicCategoriesWithHierarchy,
  CampaignData,
} from '../../../services/ecommerceApi';
import {
  CAMPAIGN_SPECIAL_DAYS,
  getSpecialDayLabel,
  suggestSpecialDayDates,
} from './campaignSpecialDays';

interface CampaignManagementProps {
  companyId: string | null;
}

type CampaignType = 'ProductDiscount' | 'Coupon' | 'CartDiscount' | 'BuyXPayY' | 'FreeShipping';

interface OptionItem {
  id: string;
  name: string;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  active: 'success',
  upcoming: 'info',
  expired: 'default',
  passive: 'warning',
};

const emptyForm = (): CampaignData => ({
  name: '',
  description: '',
  type: 'ProductDiscount',
  types: ['ProductDiscount'],
  startDate: '',
  endDate: '',
  discountType: 'Percentage',
  discountValue: 0,
  maxDiscountAmount: null,
  minCartAmount: null,
  buyQuantity: null,
  payQuantity: null,
  couponCode: '',
  usageLimit: null,
  usageLimitPerCustomer: null,
  scopeType: 'All',
  priority: 0,
  specialDay: '',
  isActive: true,
  productIds: [],
  categoryIds: [],
  brandIds: [],
});

const normalizeTypes = (c: any): CampaignType[] => {
  if (Array.isArray(c?.types) && c.types.length > 0) {
    return c.types.filter((t: string) =>
      ['ProductDiscount', 'Coupon', 'CartDiscount', 'BuyXPayY', 'FreeShipping'].includes(t)
    ) as CampaignType[];
  }
  if (c?.type) return [c.type as CampaignType];
  return ['ProductDiscount'];
};

const toLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.03 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
  },
};

const CampaignManagement: React.FC<CampaignManagementProps> = ({ companyId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [specialDayFilter, setSpecialDayFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [products, setProducts] = useState<OptionItem[]>([]);
  const [categories, setCategories] = useState<OptionItem[]>([]);
  const [brands, setBrands] = useState<OptionItem[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CampaignData>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

  const showToast = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const typeMeta = useMemo(
    () => ({
      ProductDiscount: { label: tt('Ürün İndirimi', 'Product Discount'), icon: <LocalOfferIcon />, desc: tt('Seçili ürün/kategori/markada yüzde veya tutar indirimi', 'Percentage or amount off selected products/categories/brands') },
      Coupon: { label: tt('Kupon Kodu', 'Coupon Code'), icon: <ConfirmationNumberIcon />, desc: tt('Sepette kod girerek indirim', 'Discount by entering a code at checkout') },
      CartDiscount: { label: tt('Sepet İndirimi', 'Cart Discount'), icon: <ShoppingCartIcon />, desc: tt('Sepet tutarı eşiğine göre indirim', 'Discount based on cart total threshold') },
      BuyXPayY: { label: tt('X Al Y Öde', 'Buy X Pay Y'), icon: <RedeemIcon />, desc: tt('Çok al az öde kampanyası', 'Buy more, pay less') },
      FreeShipping: { label: tt('Ücretsiz Kargo', 'Free Shipping'), icon: <LocalShippingIcon />, desc: tt('Eşik üzerinde ücretsiz kargo', 'Free shipping above a threshold') },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTr]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCampaigns(companyId || undefined);
      const data = res?.data || [];
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[CampaignManagement] Kampanyalar yüklenemedi:', err);
      showToast(tt('Kampanyalar yüklenemedi.', 'Failed to load campaigns.'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId) load();
  }, [companyId, load]);

  // Ürün/kategori/marka seçicileri için katalog verisi (dialog açıldığında bir kez).
  const ensureCatalog = useCallback(async () => {
    if (!companyId) return;
    try {
      if (products.length === 0 || brands.length === 0) {
        const items = await getEcommerceProducts(companyId);
        const list = Array.isArray(items) ? items : [];
        if (products.length === 0) {
          setProducts(
            list.map((p: any) => ({ id: String(p.id), name: p.name || p.stockCode || String(p.id) }))
          );
        }
        if (brands.length === 0) {
          // Inventory BrandId — PricingService ürün.BrandId ile eşleşmeli
          const brandMap = new Map<string, string>();
          list.forEach((p: any) => {
            const id = p.brandId != null ? String(p.brandId).trim() : '';
            if (!id) return;
            const name = (p.brandName && String(p.brandName).trim()) || id;
            if (!brandMap.has(id)) brandMap.set(id, name);
          });
          setBrands(Array.from(brandMap.entries()).map(([id, name]) => ({ id, name })));
        }
      }
      if (categories.length === 0) {
        const cats = await getPublicCategoriesWithHierarchy(companyId);
        setCategories(
          (Array.isArray(cats) ? cats : []).map((c: any) => ({ id: String(c.id), name: c.name || String(c.id) }))
        );
      }
    } catch (err) {
      console.error('[CampaignManagement] Katalog yüklenemedi:', err);
    }
  }, [companyId, products.length, categories.length, brands.length]);

  const openCreate = async () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
    await ensureCatalog();
  };

  const openEdit = async (c: any) => {
    setEditingId(c.id);
    const types = normalizeTypes(c);
    setForm({
      ...emptyForm(),
      ...c,
      types,
      type: types[0] || 'ProductDiscount',
      startDate: toLocalInput(c.startDate),
      endDate: toLocalInput(c.endDate),
      productIds: c.productIds || [],
      categoryIds: c.categoryIds || [],
      brandIds: c.brandIds || [],
    });
    setDialogOpen(true);
    await ensureCatalog();
  };

  const setField = <K extends keyof CampaignData>(key: K, value: CampaignData[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const selectedTypes = (form.types && form.types.length > 0 ? form.types : [form.type]) as CampaignType[];
  const hasType = (key: CampaignType) => selectedTypes.includes(key);

  const toggleType = (key: CampaignType) => {
    setForm((prev) => {
      const current = (prev.types && prev.types.length > 0 ? prev.types : [prev.type]) as CampaignType[];
      const exists = current.includes(key);
      let next: CampaignType[];
      if (exists) {
        if (current.length === 1) return prev; // en az bir tür kalsın
        next = current.filter((t) => t !== key);
      } else {
        next = [...current, key];
      }
      const primary =
        next.includes('ProductDiscount') ? 'ProductDiscount' : next[0];
      return { ...prev, types: next, type: primary };
    });
  };

  const handleSpecialDayChange = (key: string) => {
    const suggested = suggestSpecialDayDates(key);
    setForm((prev) => ({
      ...prev,
      specialDay: key || '',
      ...(suggested
        ? { startDate: suggested.startDate, endDate: suggested.endDate }
        : {}),
    }));
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return tt('Kampanya adı zorunludur.', 'Campaign name is required.');
    if (!form.startDate || !form.endDate) return tt('Başlangıç ve bitiş tarihi zorunludur.', 'Start and end dates are required.');
    if (new Date(form.endDate) < new Date(form.startDate)) return tt('Bitiş tarihi başlangıçtan önce olamaz.', 'End date cannot be before start date.');
    if (selectedTypes.length === 0) return tt('En az bir kampanya türü seçin.', 'Select at least one campaign type.');
    if (hasType('Coupon') && !form.couponCode?.trim()) return tt('Kupon kodu zorunludur.', 'Coupon code is required.');
    if (hasType('BuyXPayY')) {
      const b = Number(form.buyQuantity), p = Number(form.payQuantity);
      if (!b || !p || p >= b) return tt("'Al' adedi 'öde' adedinden büyük olmalıdır.", "'Buy' quantity must exceed 'pay' quantity.");
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) { showToast(error, 'error'); return; }

    setSaving(true);
    try {
      const types = selectedTypes.length > 0 ? selectedTypes : ['ProductDiscount'];
      const payload: CampaignData = {
        ...form,
        companyId: companyId || undefined,
        types,
        type: types.includes('ProductDiscount') ? 'ProductDiscount' : types[0],
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        discountValue: Number(form.discountValue) || 0,
        maxDiscountAmount: form.maxDiscountAmount != null && form.maxDiscountAmount !== ('' as any) ? Number(form.maxDiscountAmount) : null,
        minCartAmount: form.minCartAmount != null && form.minCartAmount !== ('' as any) ? Number(form.minCartAmount) : null,
        buyQuantity: form.buyQuantity ? Number(form.buyQuantity) : null,
        payQuantity: form.payQuantity ? Number(form.payQuantity) : null,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
        usageLimitPerCustomer: form.usageLimitPerCustomer ? Number(form.usageLimitPerCustomer) : null,
        priority: Number(form.priority) || 0,
        specialDay: form.specialDay?.trim() || null,
      };

      if (editingId) {
        await updateCampaign(editingId, payload);
        showToast(tt('Kampanya güncellendi.', 'Campaign updated.'));
      } else {
        await createCampaign(payload);
        showToast(tt('Kampanya oluşturuldu.', 'Campaign created.'));
      }
      setDialogOpen(false);
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message || tt('Kampanya kaydedilemedi.', 'Failed to save campaign.');
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (c: any) => {
    try {
      await toggleCampaign(c.id, !c.isActive, companyId || undefined);
      await load();
    } catch (err) {
      showToast(tt('Durum güncellenemedi.', 'Failed to update status.'), 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCampaign(deleteTarget.id, companyId || undefined);
      showToast(tt('Kampanya silindi.', 'Campaign deleted.'));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      showToast(tt('Kampanya silinemedi.', 'Failed to delete campaign.'), 'error');
    }
  };

  const statusLabel = (status: string) =>
    ({
      active: tt('Aktif', 'Active'),
      upcoming: tt('Yaklaşan', 'Upcoming'),
      expired: tt('Süresi Doldu', 'Expired'),
      passive: tt('Pasif', 'Passive'),
    } as Record<string, string>)[status] || status;

  const typeLabel = (type: string) => (typeMeta as any)[type]?.label || type;
  const typesLabel = (c: any) => {
    const types = normalizeTypes(c);
    return types.map((t) => typeLabel(t)).join(' + ');
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(localeCode, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const discountLabel = (c: any) => {
    if (c.type === 'FreeShipping') return tt('Ücretsiz Kargo', 'Free Shipping');
    if (c.type === 'BuyXPayY') return `${c.buyQuantity} → ${c.payQuantity}`;
    if (c.discountType === 'Percentage') return `%${c.discountValue}`;
    return `${c.discountValue} ${tt('TL', 'TRY')}`;
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      const day = String(c.specialDay || '');
      if (specialDayFilter === 'none' && day) return false;
      if (specialDayFilter !== 'all' && specialDayFilter !== 'none' && day !== specialDayFilter) return false;
      if (
        term &&
        !(`${c.name} ${c.couponCode || ''} ${getSpecialDayLabel(c.specialDay, isTr)}`.toLowerCase().includes(term))
      ) {
        return false;
      }
      return true;
    });
  }, [campaigns, statusFilter, specialDayFilter, searchTerm, isTr]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: campaigns.length, active: 0, upcoming: 0, expired: 0, passive: 0 };
    campaigns.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
    return counts;
  }, [campaigns]);

  const showDiscountValue = hasType('ProductDiscount') || hasType('Coupon') || hasType('CartDiscount');
  const showScope = hasType('ProductDiscount') || hasType('BuyXPayY');
  const showMinCart = hasType('Coupon') || hasType('CartDiscount') || hasType('FreeShipping');

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">{tt('Kampanyalar', 'Campaigns')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tt('E-ticaret sitenizdeki indirim ve kampanyaları yönetin', 'Manage discounts and campaigns on your store')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={tt('Yenile', 'Refresh')}>
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {tt('Yeni Kampanya', 'New Campaign')}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => { setStatusFilter(v); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label={`${tt('Tümü', 'All')} (${statusCounts.all})`} />
          <Tab value="active" label={`${tt('Aktif', 'Active')} (${statusCounts.active || 0})`} />
          <Tab value="upcoming" label={`${tt('Yaklaşan', 'Upcoming')} (${statusCounts.upcoming || 0})`} />
          <Tab value="expired" label={`${tt('Süresi Doldu', 'Expired')} (${statusCounts.expired || 0})`} />
          <Tab value="passive" label={`${tt('Pasif', 'Passive')} (${statusCounts.passive || 0})`} />
        </Tabs>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          sx={{ flex: 1, minWidth: 220 }}
          placeholder={tt('Kampanya adı veya kupon kodu ara...', 'Search by name or coupon code...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{tt('Özel Gün / Sezon', 'Special Day / Season')}</InputLabel>
          <Select
            label={tt('Özel Gün / Sezon', 'Special Day / Season')}
            value={specialDayFilter}
            onChange={(e) => { setSpecialDayFilter(String(e.target.value)); setPage(0); }}
          >
            <MenuItem value="all">{tt('Tüm özel günler', 'All special days')}</MenuItem>
            <MenuItem value="none">{tt('Özel gün yok', 'No special day')}</MenuItem>
            {CAMPAIGN_SPECIAL_DAYS.filter((d) => d.key).map((day) => (
              <MenuItem key={day.key} value={day.key}>
                {isTr ? day.labelTr : day.labelEn}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${statusFilter}-${specialDayFilter}-${searchTerm}-${page}`}
            variants={listVariants}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
          >
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{tt('Ad', 'Name')}</TableCell>
                      <TableCell>{tt('Tür', 'Type')}</TableCell>
                      <TableCell>{tt('Özel Gün', 'Special Day')}</TableCell>
                      <TableCell>{tt('İndirim', 'Discount')}</TableCell>
                      <TableCell>{tt('Tarih Aralığı', 'Date Range')}</TableCell>
                      <TableCell align="center">{tt('Kullanım', 'Usage')}</TableCell>
                      <TableCell align="center">{tt('Durum', 'Status')}</TableCell>
                      <TableCell align="center">{tt('Aktif', 'Active')}</TableCell>
                      <TableCell align="right">{tt('İşlemler', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          {tt(
                            'Kampanya bulunamadı. “Yeni Kampanya” ile özel gün/sezon seçerek oluşturabilirsiniz.',
                            'No campaigns found. Create one with “New Campaign” and pick a special day/season.'
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          component={motion.tr as any}
                          variants={rowVariants}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                            {c.couponCode && (
                              <Chip size="small" label={c.couponCode} variant="outlined" sx={{ mt: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ lineHeight: 1.35 }}>
                              {typesLabel(c)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {c.specialDay ? (
                              <Chip size="small" color="secondary" variant="outlined" label={getSpecialDayLabel(c.specialDay, isTr)} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{discountLabel(c)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">{formatDate(c.startDate)}</Typography>
                            <Typography variant="caption" color="text.secondary">→ {formatDate(c.endDate)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            {c.usedCount || 0}{c.usageLimit ? ` / ${c.usageLimit}` : ''}
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={statusLabel(c.status)} color={STATUS_COLORS[c.status] || 'default'} />
                          </TableCell>
                          <TableCell align="center">
                            <Switch size="small" checked={!!c.isActive} onChange={() => handleToggle(c)} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={tt('Düzenle', 'Edit')}>
                              <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title={tt('Sil', 'Delete')}>
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(c)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Paper>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Oluştur / Düzenle */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? tt('Kampanyayı Düzenle', 'Edit Campaign') : tt('Yeni Kampanya', 'New Campaign')}
        </DialogTitle>
        <DialogContent dividers>
          {/* Tür seçimi — birden fazla seçilebilir (Trendyol tarzı) */}
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{tt('Kampanya Türleri', 'Campaign Types')}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {tt(
              'Birden fazla mekanik seçebilirsiniz (ör. ürün indirimi + ücretsiz kargo).',
              'You can select multiple mechanics (e.g. product discount + free shipping).'
            )}
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {(Object.keys(typeMeta) as CampaignType[]).map((key) => {
              const meta = (typeMeta as any)[key];
              const selected = hasType(key);
              return (
                <Grid item xs={6} sm={4} md={2.4} key={key}>
                  <Card variant="outlined" sx={{ borderColor: selected ? 'primary.main' : undefined, borderWidth: selected ? 2 : 1, height: '100%', bgcolor: selected ? 'rgba(25,118,210,0.04)' : undefined }}>
                    <CardActionArea
                      onClick={() => toggleType(key)}
                      sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.5 }}
                    >
                      <Box sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>{meta.icon}</Box>
                      <Typography variant="caption" fontWeight={600}>{meta.label}</Typography>
                      {selected && (
                        <Chip size="small" color="primary" label={tt('Seçili', 'Selected')} sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Typography variant="caption" color="text.secondary">
            {selectedTypes.map((t) => (typeMeta as any)[t]?.desc).filter(Boolean).join(' · ')}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label={tt('Kampanya Adı', 'Campaign Name')}
                fullWidth size="small" required
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={tt('Öncelik', 'Priority')}
                type="number" fullWidth size="small"
                value={form.priority ?? 0}
                onChange={(e) => setField('priority', Number(e.target.value) as any)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={tt('Açıklama', 'Description')}
                fullWidth size="small" multiline minRows={2}
                value={form.description || ''}
                onChange={(e) => setField('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Özel Gün / Sezon', 'Special Day / Season')}</InputLabel>
                <Select
                  label={tt('Özel Gün / Sezon', 'Special Day / Season')}
                  value={form.specialDay || ''}
                  onChange={(e) => handleSpecialDayChange(String(e.target.value))}
                >
                  {CAMPAIGN_SPECIAL_DAYS.map((day) => (
                    <MenuItem key={day.key || 'none'} value={day.key}>
                      {isTr ? day.labelTr : day.labelEn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {tt(
                  'Seçince önerilen tarih aralığı otomatik doldurulur; isterseniz değiştirebilirsiniz.',
                  'Selecting a day auto-fills suggested dates; you can edit them anytime.'
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={tt('Başlangıç', 'Start')}
                type="datetime-local" fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={form.startDate}
                onChange={(e) => setField('startDate', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tt('Bitiş', 'End')}
                type="datetime-local" fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={form.endDate}
                onChange={(e) => setField('endDate', e.target.value)}
              />
            </Grid>

            {/* Kupon kodu */}
            {hasType('Coupon') && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={tt('Kupon Kodu', 'Coupon Code')}
                  fullWidth size="small" required
                  value={form.couponCode || ''}
                  onChange={(e) => setField('couponCode', e.target.value.toUpperCase())}
                />
              </Grid>
            )}

            {/* İndirim türü + değeri */}
            {showDiscountValue && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{tt('İndirim Türü', 'Discount Type')}</InputLabel>
                    <Select
                      label={tt('İndirim Türü', 'Discount Type')}
                      value={form.discountType}
                      onChange={(e) => setField('discountType', e.target.value)}
                    >
                      <MenuItem value="Percentage">{tt('Yüzde (%)', 'Percentage (%)')}</MenuItem>
                      <MenuItem value="Amount">{tt('Tutar', 'Amount')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tt('İndirim Değeri', 'Discount Value')}
                    type="number" fullWidth size="small"
                    value={form.discountValue}
                    onChange={(e) => setField('discountValue', Number(e.target.value) as any)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">{form.discountType === 'Percentage' ? '%' : tt('TL', 'TRY')}</InputAdornment>,
                    }}
                  />
                </Grid>
                {form.discountType === 'Percentage' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={tt('Maks. İndirim Tutarı (ops.)', 'Max Discount Amount (opt.)')}
                      type="number" fullWidth size="small"
                      value={form.maxDiscountAmount ?? ''}
                      onChange={(e) => setField('maxDiscountAmount', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* X al Y öde */}
            {hasType('BuyXPayY') && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label={tt('Al (X)', 'Buy (X)')}
                    type="number" fullWidth size="small"
                    value={form.buyQuantity ?? ''}
                    onChange={(e) => setField('buyQuantity', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label={tt('Öde (Y)', 'Pay (Y)')}
                    type="number" fullWidth size="small"
                    value={form.payQuantity ?? ''}
                    onChange={(e) => setField('payQuantity', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                  />
                </Grid>
              </>
            )}

            {/* Minimum sepet tutarı */}
            {showMinCart && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label={tt('Min. Sepet Tutarı', 'Min Cart Amount')}
                  type="number" fullWidth size="small"
                  value={form.minCartAmount ?? ''}
                  onChange={(e) => setField('minCartAmount', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                />
              </Grid>
            )}

            {/* Kupon limitleri */}
            {hasType('Coupon') && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label={tt('Toplam Limit', 'Total Limit')}
                    type="number" fullWidth size="small"
                    value={form.usageLimit ?? ''}
                    onChange={(e) => setField('usageLimit', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    label={tt('Kişi Başı Limit', 'Per-Customer Limit')}
                    type="number" fullWidth size="small"
                    value={form.usageLimitPerCustomer ?? ''}
                    onChange={(e) => setField('usageLimitPerCustomer', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                  />
                </Grid>
              </>
            )}

            {/* Kapsam */}
            {showScope && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{tt('Kapsam', 'Scope')}</InputLabel>
                    <Select
                      label={tt('Kapsam', 'Scope')}
                      value={form.scopeType}
                      onChange={(e) => setField('scopeType', e.target.value)}
                    >
                      <MenuItem value="All">{tt('Tüm Ürünler', 'All Products')}</MenuItem>
                      <MenuItem value="Products">{tt('Seçili Ürünler', 'Selected Products')}</MenuItem>
                      <MenuItem value="Categories">{tt('Seçili Kategoriler', 'Selected Categories')}</MenuItem>
                      <MenuItem value="Brands">{tt('Seçili Markalar', 'Selected Brands')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {form.scopeType === 'Products' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple size="small"
                      options={products}
                      getOptionLabel={(o) => o.name}
                      value={products.filter((p) => (form.productIds || []).includes(p.id))}
                      onChange={(_, val) => setField('productIds', val.map((v) => v.id))}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(params) => <TextField {...params} label={tt('Ürünler', 'Products')} placeholder={tt('Ürün seç...', 'Select products...')} />}
                    />
                  </Grid>
                )}
                {form.scopeType === 'Categories' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple size="small"
                      options={categories}
                      getOptionLabel={(o) => o.name}
                      value={categories.filter((c) => (form.categoryIds || []).includes(c.id))}
                      onChange={(_, val) => setField('categoryIds', val.map((v) => v.id))}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(params) => <TextField {...params} label={tt('Kategoriler', 'Categories')} placeholder={tt('Kategori seç...', 'Select categories...')} />}
                    />
                  </Grid>
                )}
                {form.scopeType === 'Brands' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple size="small"
                      options={brands}
                      getOptionLabel={(o) => o.name}
                      value={brands.filter((b) => (form.brandIds || []).includes(b.id))}
                      onChange={(_, val) => setField('brandIds', val.map((v) => v.id))}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(params) => <TextField {...params} label={tt('Markalar', 'Brands')} placeholder={tt('Marka seç...', 'Select brands...')} />}
                    />
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch checked={form.isActive} onChange={(e) => setField('isActive', e.target.checked)} />
                <Typography variant="body2">{tt('Kampanya aktif', 'Campaign active')}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>{tt('İptal', 'Cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{tt('Kampanyayı Sil', 'Delete Campaign')}</DialogTitle>
        <DialogContent>
          <Typography>
            {tt('Bu kampanyayı silmek istediğinize emin misiniz?', 'Are you sure you want to delete this campaign?')}
          </Typography>
          {deleteTarget && <Typography fontWeight={600} sx={{ mt: 1 }}>{deleteTarget.name}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{tt('İptal', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>{tt('Sil', 'Delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignManagement;
